import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Drawer, useMediaQuery } from '@mui/material';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import buildNavConfig from './SidebarConfig';
import CreatePopover from './CreatePopover';

type NavConfig = {
  subheader: string;
  items: {
    title: string;
    path?: string;
    icon?: React.JSX.Element;
    exact?: boolean;
    info?: React.JSX.Element;
  }[];
}[];

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 88;
const HEADER_HEIGHT = 64;

const RootStyle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }
}));

type DashboardSidebarProps = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
  onToggleCollapse?: VoidFunction;
  isCollapsed: boolean;
  navConfig?: NavConfig;
  footer?: React.ReactNode;
};

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
  onToggleCollapse,
  isCollapsed,
  navConfig: navConfigProp,
  footer
}: DashboardSidebarProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { pathname } = useLocation();

  const enrichedConfig = useMemo(() => navConfigProp ?? buildNavConfig([]), [navConfigProp]);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (collapsed = false) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Scrollbar
        sx={{
          flexGrow: 1,
          '& .simplebar-content': { display: 'flex', flexDirection: 'column' }
        }}
      >
        <NavSection
          navConfig={enrichedConfig}
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          leadingAction={<CreatePopover collapsed={collapsed} />}
        />
      </Scrollbar>
      {footer && <Box sx={{ px: 2, pb: 2, pt: 1, flexShrink: 0 }}>{footer}</Box>}
    </Box>
  );

  return (
    <RootStyle collapsed={isDesktop ? isCollapsed : false}>
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          sx={{
            pointerEvents: 'none',
            '& .MuiBackdrop-root': { top: HEADER_HEIGHT, pointerEvents: 'auto' }
          }}
          slotProps={{
            paper: {
              sx: {
                width: DRAWER_WIDTH,
                bgcolor: 'background.default',
                top: HEADER_HEIGHT,
                height: `calc(100% - ${HEADER_HEIGHT}px)`,
                pointerEvents: 'auto'
              }
            }
          }}
        >
          {renderContent(false)}
        </Drawer>
      )}
      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          slotProps={{
            paper: {
              sx: {
                width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
                top: HEADER_HEIGHT,
                height: `calc(100% - ${HEADER_HEIGHT}px)`,
                bgcolor: 'background.default',
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen
                })
              }
            }
          }}
        >
          {renderContent(isCollapsed)}
        </Drawer>
      )}
    </RootStyle>
  );
}
